<template>
    <section class="w-full py-16 md:py-24 bg-gradient-to-b from-eblack-950 to-eblack-900 relative overflow-hidden flex items-center justify-center font-modulus">

        <!-- Glowing gradient accents -->
        <div class="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(239,78,32,0.1),transparent_50%)] pointer-events-none"></div>
        <div class="absolute top-1/2 left-1/4 w-72 h-72 bg-eblue-500/5 rounded-full blur-3xl pointer-events-none"></div>

        <div class="container px-4 md:px-6 relative z-10 flex flex-col items-center">

            <!-- Glassmorphic Container Box -->
            <div class="w-full max-w-3xl bg-ewhite-50/[0.02] backdrop-blur-lg border border-ewhite-50/10 rounded-3xl p-8 md:p-12 shadow-2xl flex flex-col items-center text-center space-y-8">

                <!-- Headers -->
                <div class="space-y-3">
                    <h2 class="text-3xl md:text-5xl font-bold tracking-tight text-white font-modulus-semi-bold">
                        {{ $t('cta_section.title') }}
                    </h2>
                    <p class="mx-auto max-w-[550px] text-ewhite-300 text-sm md:text-base leading-relaxed">
                        {{ $t('cta_section.subtitle') }}
                    </p>
                </div>

                <!-- Form -->
                <div class="w-full max-w-md">
                    <form @submit.prevent="sendEmail" class="flex flex-col sm:flex-row gap-3 w-full">
                        <input
                            v-model="email"
                            type="email"
                            :placeholder="$t('cta_section.email_placeholder')"
                            required
                            :disabled="isLoading"
                            class="flex-grow rounded-full border border-ewhite-50/10 bg-ewhite-50/[0.05] px-5 py-3 text-sm text-white placeholder-ewhite-400 outline-none transition-all duration-300 focus:bg-ewhite-50/[0.09] focus:border-eorange-500/50 focus:ring-4 focus:ring-eorange-500/10 disabled:opacity-50"
                        />
                        <button
                            type="submit"
                            :disabled="isLoading || !email"
                            class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-bold bg-eorange-500 text-white hover:bg-eorange-600 shadow-lg shadow-eorange-500/10 hover:shadow-eorange-500/25 px-6 py-3 transition-all duration-300 hover:scale-[1.03] disabled:opacity-50 shrink-0 cursor-pointer"
                        >
                            <span v-if="!isLoading">{{ $t('cta_section.button_text') }}</span>
                            <span v-else>{{ $t('cta_section.sending') }}</span>
                            <div v-if="isLoading" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        </button>
                    </form>
                    <p class="text-[10px] text-ewhite-400 mt-3 block font-semibold">
                        {{ $t('cta_section.footer_note') }}
                    </p>
                </div>

                <!-- Features list -->
                <div class="flex flex-wrap items-center justify-center gap-6 text-ewhite-300 text-xs">
                    <div v-for="(text, index) in features" :key="index" class="flex items-center group cursor-default">
                        <IconCircleCheck
                            stroke-width="2"
                            class="h-4 w-4 mr-2 text-eorange-500 transition-transform duration-300 group-hover:scale-125 shrink-0"
                        />
                        <span class="transition-colors duration-300 group-hover:text-white font-semibold">{{ $t(`${text}`) }}</span>
                    </div>
                </div>

            </div>

        </div>
    </section>
</template>

<script>
import { i18n } from "@/i18n";
import { IconCircleCheck } from '@tabler/icons-vue';
import emailjs from '@emailjs/browser';
import posthog from 'posthog-js';
import { useToast } from 'vue-toastification';
import { emailjsConfig, validateEmailjsConfig } from '../../../config/emailjs.config.js';

export default {
    name: 'CtaSection',
    setup() {
        const toast = useToast();
        return { toast };
    },
    data() {
        return {
            email: '',
            isLoading: false,
            features: ["cta_section.without_commitment", "cta_section.support"]
        };
    },
    mounted() {
        // Validar configuración de EmailJS al montar el componente
        if (!validateEmailjsConfig()) {
            console.warn('EmailJS no está configurado. Las funciones de email no funcionarán correctamente.');
        }
    },
    methods: {
        async sendEmail() {
            if (!this.email || this.isLoading) return;

            this.isLoading = true;

            try {
                // Validar email
                if (!this.validateEmail()) {
                    this.toast.error(this.$t('cta_section.invalid_email'));
                    return;
                }

                // Validar configuración de EmailJS
                if (!validateEmailjsConfig()) {
                    this.toast.error(i18n.global.t("marketing.auto.emailjs_no_esta_configurado_correctamente"));
                    return;
                }

                // Preparar los datos del template para EmailJS
                const templateParams = {
                    from_name: 'Usuario desde Web Emanta',
                    email_id: this.email,
                    message: this.buildEmailMessage(),
                    to_name: 'Equipo Emanta',
                    subject: 'Nueva Solicitud de Demo - Emanta',
                    reply_to: this.email,
                    language: this.$i18n.locale,
                    request_date: new Date().toLocaleString(this.$i18n.locale),
                    website: window.location.origin
                };

                // Enviar el email usando EmailJS
                const response = await emailjs.send(
                    emailjsConfig.serviceId,
                    emailjsConfig.demoTemplateId,
                    templateParams,
                    emailjsConfig.publicKey
                );

                if (response.status === 200) {
                    // Éxito
                    this.toast.success(this.$t('cta_section.success_message'));
                    this.email = ''; // Limpiar el formulario
                    this.trackDemoRequest(); // Función para analytics
                } else {
                    throw new Error('Error en el envío');
                }

            } catch (error) {
                console.error('Error sending email:', error);
                this.toast.error(this.$t('cta_section.error_message'));
            } finally {
                this.isLoading = false;
            }
        },

        // Construir el mensaje del email
        buildEmailMessage() {
            return `Nueva solicitud de demo desde la página web de Emanta.

📧 Email del interesado: ${this.email}
📅 Fecha de solicitud: ${new Date().toLocaleString(this.$i18n.locale)}
🌐 Idioma preferido: ${this.$i18n.locale === 'es' ? 'Español' : 'English'}
💻 Origen: Sección CTA del sitio web
🔗 URL: ${window.location.href}

Por favor, contactar al usuario para programar una demo personalizada de Emanta.

---
Este email fue enviado automáticamente desde el sitio web de Emanta.`;
        },

        // Función para tracking de analytics
        trackDemoRequest() {
            try {
                // PostHog tracking
                posthog.capture('demo_requested', {
                    email: this.email,
                    source: 'cta_section',
                    language: this.$i18n.locale,
                    url: window.location.href
                });

                // Google Analytics tracking (si está disponible)
                if (window.gtag) {
                    window.gtag('event', 'demo_request', {
                        event_category: 'engagement',
                        event_label: 'cta_section',
                        value: 1
                    });
                }
            } catch (error) {
                console.warn('Error en tracking:', error);
            }
        },

        // Validar email
        validateEmail() {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(this.email);
        }
    },
    components: {
        IconCircleCheck
    }
}
</script>

<style lang="scss" scoped></style>